import React, { useState, useEffect } from 'react';
import { supabaseService } from '../services/supabaseService';

interface DeletionRequest {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_photo_url: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  requested_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  admin_notes: string | null;
  created_at: string;
}

interface ConfirmModal {
  isOpen: boolean;
  request: DeletionRequest | null;
  action: 'approve' | 'reject' | null;
}

const AccountDeletionRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [confirmModal, setConfirmModal] = useState<ConfirmModal>({
    isOpen: false,
    request: null,
    action: null
  });
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Durum stilleri
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return { color: '#f39c12', bg: '#fef9e7', text: '⏳ Bekliyor' };
      case 'approved':
        return { color: '#27ae60', bg: '#eafaf1', text: '✅ Onaylandı' };
      case 'rejected':
        return { color: '#e74c3c', bg: '#fdeaea', text: '❌ Reddedildi' };
      case 'completed':
        return { color: '#3498db', bg: '#ebf3fd', text: '🗑️ Tamamlandı' };
      default:
        return { color: '#95a5a6', bg: '#f8f9fa', text: status };
    }
  };

  // Tarih formatı
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Geçersiz tarih';
    }
  };

  // Başvuruları yükle
  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await supabaseService.getAccountDeletionRequests();
      setRequests(data);
    } catch (err) {
      setError('Hesap silme başvuruları yüklenirken hata oluştu: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Modal aç
  const openConfirmModal = (request: DeletionRequest, action: 'approve' | 'reject') => {
    setConfirmModal({
      isOpen: true,
      request,
      action
    });
    setAdminNotes('');
  };

  // Modal kapat
  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      request: null,
      action: null
    });
    setAdminNotes('');
  };

  // Başvuruyu işle
  const processRequest = async () => {
    if (!confirmModal.request || !confirmModal.action) return;

    try {
      setProcessing(true);
      
      if (confirmModal.action === 'approve') {
        await supabaseService.approveAccountDeletion(
          confirmModal.request.id,
          confirmModal.request.user_id,
          adminNotes || 'Admin tarafından onaylandı ve hesap silindi'
        );
      } else {
        await supabaseService.rejectAccountDeletion(
          confirmModal.request.id,
          adminNotes || 'Admin tarafından reddedildi'
        );
      }
      
      await loadRequests();
      closeConfirmModal();
    } catch (err) {
      setError('İşlem sırasında hata oluştu: ' + (err as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  // Filtrelenmiş başvurular
  const filteredRequests = requests.filter(req => {
    if (filterStatus === 'all') return true;
    return req.status === filterStatus;
  });

  // İstatistikler
  const stats = {
    total: requests.length,
    pending: requests.filter(req => req.status === 'pending').length,
    approved: requests.filter(req => req.status === 'approved').length,
    rejected: requests.filter(req => req.status === 'rejected').length,
    completed: requests.filter(req => req.status === 'completed').length,
  };

  useEffect(() => {
    loadRequests();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>⏳</div>
        <div>Hesap silme başvuruları yükleniyor...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem' 
      }}>
        <h1 style={{ color: '#2c3e50', margin: 0 }}>
          🗑️ Hesap Silme Başvuruları
        </h1>
        <button
          onClick={loadRequests}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          🔄 Yenile
        </button>
      </div>

      {/* İstatistik kartları */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2c3e50' }}>{stats.total}</div>
          <div style={{ color: '#7f8c8d' }}>Toplam Başvuru</div>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center',
          border: stats.pending > 0 ? '2px solid #f39c12' : 'none'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f39c12' }}>{stats.pending}</div>
          <div style={{ color: '#7f8c8d' }}>⏳ Bekleyen</div>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3498db' }}>{stats.completed}</div>
          <div style={{ color: '#7f8c8d' }}>🗑️ Silinen</div>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e74c3c' }}>{stats.rejected}</div>
          <div style={{ color: '#7f8c8d' }}>❌ Reddedilen</div>
        </div>
      </div>

      {/* Filtre */}
      <div style={{ marginBottom: '1.5rem' }}>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '0.5rem',
            borderRadius: '6px',
            border: '1px solid #ddd',
            backgroundColor: 'white',
            fontSize: '1rem'
          }}
        >
          <option value="all">Tüm Başvurular ({stats.total})</option>
          <option value="pending">Bekleyenler ({stats.pending})</option>
          <option value="completed">Silinenler ({stats.completed})</option>
          <option value="rejected">Reddedilenler ({stats.rejected})</option>
        </select>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fdeaea',
          color: '#e74c3c',
          padding: '1rem',
          borderRadius: '6px',
          marginBottom: '1rem',
          border: '1px solid #f1c0c0'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Başvuru listesi */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {filteredRequests.length === 0 ? (
          <div style={{ 
            padding: '3rem', 
            textAlign: 'center', 
            color: '#7f8c8d' 
          }}>
            📭 Hiç başvuru bulunamadı
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Kullanıcı</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Sebep</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Başvuru Tarihi</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Durum</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((req) => {
                  const statusStyle = getStatusStyle(req.status);

                  return (
                    <tr key={req.id} style={{ borderBottom: '1px solid #f1f2f6' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {req.user_photo_url ? (
                            <img 
                              src={req.user_photo_url} 
                              alt={req.user_name}
                              style={{ 
                                width: '40px', 
                                height: '40px', 
                                borderRadius: '50%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.2rem'
                            }}>
                              👤
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{req.user_name || 'İsimsiz'}</div>
                            <div style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>
                              ID: {req.user_id.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontSize: '0.9rem' }}>{req.user_email || '-'}</div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ 
                          fontSize: '0.85rem', 
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {req.reason || '-'}
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontSize: '0.9rem' }}>{formatDate(req.requested_at)}</div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 'bold'
                        }}>
                          {statusStyle.text}
                        </span>
                        {req.admin_notes && (
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#7f8c8d',
                            marginTop: '0.25rem',
                            fontStyle: 'italic'
                          }}>
                            Not: {req.admin_notes.substring(0, 30)}...
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {req.status === 'pending' && (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => openConfirmModal(req, 'approve')}
                              style={{
                                padding: '0.4rem 0.75rem',
                                fontSize: '0.8rem',
                                backgroundColor: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                              }}
                            >
                              🗑️ Sil
                            </button>
                            <button
                              onClick={() => openConfirmModal(req, 'reject')}
                              style={{
                                padding: '0.4rem 0.75rem',
                                fontSize: '0.8rem',
                                backgroundColor: '#95a5a6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              ❌ Reddet
                            </button>
                          </div>
                        )}
                        {req.status !== 'pending' && (
                          <span style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>
                            {req.reviewed_at ? formatDate(req.reviewed_at) : '-'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Onay modalı */}
      {confirmModal.isOpen && confirmModal.request && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ 
              margin: '0 0 1.5rem 0',
              color: confirmModal.action === 'approve' ? '#e74c3c' : '#95a5a6'
            }}>
              {confirmModal.action === 'approve' 
                ? '⚠️ Hesabı Silmek İstediğinize Emin Misiniz?' 
                : '❌ Başvuruyu Reddetmek İstediğinize Emin Misiniz?'}
            </h3>
            
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1rem', 
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                {confirmModal.request.user_photo_url ? (
                  <img 
                    src={confirmModal.request.user_photo_url} 
                    alt={confirmModal.request.user_name}
                    style={{ 
                      width: '50px', 
                      height: '50px', 
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}>
                    👤
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {confirmModal.request.user_name || 'İsimsiz Kullanıcı'}
                  </div>
                  <div style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                    {confirmModal.request.user_email}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                <strong>User ID:</strong> {confirmModal.request.user_id}
              </div>
            </div>

            {confirmModal.action === 'approve' && (
              <div style={{
                backgroundColor: '#fdeaea',
                border: '1px solid #f1c0c0',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <strong style={{ color: '#e74c3c' }}>⚠️ DİKKAT:</strong>
                <p style={{ margin: '0.5rem 0 0', color: '#c0392b', fontSize: '0.9rem' }}>
                  Bu işlem geri alınamaz! Kullanıcının tüm verileri (profil, mesajlar, eşleşmeler, puanlar vb.) kalıcı olarak silinecektir.
                </p>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Admin Notu:
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder={confirmModal.action === 'approve' 
                  ? 'Silme sebebi veya ek notlar...' 
                  : 'Reddetme sebebi...'}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  resize: 'vertical',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={closeConfirmModal}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f8f9fa',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                İptal
              </button>
              <button
                onClick={processRequest}
                disabled={processing}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: confirmModal.action === 'approve' ? '#e74c3c' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: processing ? 'not-allowed' : 'pointer',
                  opacity: processing ? 0.6 : 1,
                  fontWeight: 'bold'
                }}
              >
                {processing 
                  ? 'İşleniyor...' 
                  : confirmModal.action === 'approve' 
                    ? '🗑️ Hesabı Sil' 
                    : '❌ Reddet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletionRequestsPage;
